import {
  getLoanPurpose,
} from './Invoke';
import { hasHeloc } from '../../utils/Operations';

const buildSecondLoanFields = (loanAssumption) => {
  const remainingBalance = parseFloat(loanAssumption?.estimatedSecondMortgageBalance) || 0;
  const originalLoanAmount = parseFloat(loanAssumption?.estimatedSecondCurrentLoanAmount) || 0;

  // when the second loan is rolled into the new loan, it will not exist after funding, so we do not report it to OB as a remaining lien
  if (loanAssumption?.includeSecondLoan === 'Yes') {
    return { secondLienAmount: 0, helocDrawnAmount: 0, helocLineAmount: 0 };
  }

  // no second loan, all values should be 0
  if (remainingBalance <= 0) {
    return { secondLienAmount: 0, helocDrawnAmount: 0, helocLineAmount: 0 };
  }

  // if there is a heloc, we use those fields to populate the helocDrawnAmount and helocLineAmount
  if (hasHeloc(loanAssumption?.estimatedSecondCurrentLoanType)) {
    return { secondLienAmount: 0, helocDrawnAmount: remainingBalance, helocLineAmount: originalLoanAmount };
  }

  // if there is a second loan but it is not a heloc, we populate the secondLienAmount field and leave the heloc fields as 0
  return { secondLienAmount: remainingBalance, helocDrawnAmount: 0, helocLineAmount: 0 };
};

export class ProductSearchInput {
  constructor(
    occupancyInput,
    propertyTypeInput,
    numberOfUnitsInput,
    countyInput,
    salesPriceInput,
    appraisedValueInput,
    userCreditInput,
    baseLoanAmountInput,
    loanTypeInput,
    amortizationTypesInput,
    cashRequestedInput = 0,
    loanPurposeOverride = null,
    loanAssumption = null,
  ) {
    const loanPurposeInput = getLoanPurpose(loanTypeInput);
    let purpose = loanPurposeInput;
    const secondLoanFields = buildSecondLoanFields(loanAssumption);

    if (loanPurposeOverride) {
      purpose = loanPurposeOverride;
    } else if (loanPurposeInput === 'RefiCashout') {
      purpose = cashRequestedInput > 0 ? loanPurposeInput : 'RefiRateTermLimitedCO';
    }

    this.borrowerInformation = {
      citizenship: 'USCitizen',
      waiveEscrows: false,
      monthsReserves: 24,
    };

    if (loanTypeInput === 'VA') {
      this.borrowerInformation.typeOfVeteran = 'ActiveDuty';
      this.borrowerInformation.vaFirstTimeUse = true;
    }
    this.propertyInformation = {
      occupancy: occupancyInput,
      county: countyInput,
      propertyType: propertyTypeInput,
      salesPrice: salesPriceInput, // FOLLOW UP OriginalLoanAmount?
      appraisedValue: appraisedValueInput, // from currentLoanAssumption estimatedHomeValue
      state: 'CO',
      corporateRelocation: false,
      numberOfStories: 1,
      numberOfUnits: numberOfUnitsInput,
    };
    this.loanInformation = {
      representativeFICO: userCreditInput,
      loanPurpose: purpose, // only use this for now
      loanType: loanTypeInput,
      baseLoanAmount: baseLoanAmountInput,
      loanTerms: ['ThirtyYear', 'TwentyYear', 'FifteenYear'],
      desiredLockPeriod: 45, // hardcoded to be always 45, confirmed with Ben
      lienType: 'First',
      amortizationTypes: amortizationTypesInput ?? ['Fixed', 'ARM'],
      armFixedTerms: ['ThreeYear', 'FiveYear', 'SevenYear', 'TenYear'],
      automatedUnderwritingSystem: 'NotSpecified',
      borrowerPaidMI: 'Yes',
      buydown: 'None',
      cashOutAmount: cashRequestedInput,
      desiredRate: 0,
      feesIn: 'No',
      expandedApprovalLevel: 'NotApplicable',
      interestOnly: false,
      secondLienAmount: secondLoanFields.secondLienAmount,
      helocDrawnAmount: secondLoanFields.helocDrawnAmount,
      helocLineAmount: secondLoanFields.helocLineAmount,
      prepaymentPenalty: 'None',
      exemptFromVAFundingFee: loanTypeInput === 'VA',
      includeLOCompensationInPricing: 'YesLenderPaid',
      calculateTotalLoanAmount: true,
      assetDepletion: 'No',
      autoDebit: 'No',
      employeeLoan: 'No',
      communityAffordableSecond: 'No',
      reducedMI: false,
      loanLevelDebtToIncomeRatio: 43,
      customerInternalId: 'OBSearch',
      totalMonthlyQualifyingIncome: 20000, // in the future, we might want to calculate this based on user input but for now we will hardcode it.
    };
  }
}
